/**
 * The public face of each AFAR Cloud stage.
 *
 * Every AFAR client signs in to Connect as an OAuth client and returns to a fixed callback, and the
 * Connect deployment has to agree with the client on all of it: its origin, which is also the
 * issuer, the client id, and every callback it will redirect to. These are public client settings,
 * never credentials, and they are written here once. The web build, Desktop builds, and the AWS
 * stack parameters are all derived from this module, so no build can sign in to a Connect that is
 * not the deployed one.
 */

import { afarResearchHostname } from "./publicDomain.ts";

/** The Connect deployments. `preview` deploys from `main`; `production` from a Production Promotion. */
export type ConnectStage = "preview" | "production";

export interface ConnectProfile {
  /** Where Connect is served. It is also the OAuth issuer its clients sign in to. */
  readonly origin: string;
  readonly oauthClientId: string;
  /** The Runtime Web Client that signs in to this Connect. */
  readonly webOrigin: string;
  /** The delegated Route 53 zone for AFAR Network control, gateway and relay endpoints. */
  readonly workspaceDomain: string;
  /**
   * The stage's Cognito sign-in domain. Only Connect talks to it, to sign people in with Google
   * (ADR-0136); clients never see it.
   */
  readonly signInDomain: string;
  /** Where `vp run dev:web-cloud` serves a local Runtime Web Client, if this Connect accepts one. */
  readonly developmentWebOrigin?: string;
}

/** Where `vp run dev:web-cloud` serves a local Runtime Web Client; only preview accepts it. */
export const CONNECT_WEB_DEVELOPMENT_ORIGIN = "http://127.0.0.1:43128";

/** One stage's hostnames, `<name>.<preview.>afar-research.archiv.technology`. */
const stageHostnames = (stageLabel: string) => {
  const hostname = (name: string) => afarResearchHostname(`${name}${stageLabel}`);
  return {
    origin: `https://${hostname("connect")}`,
    webOrigin: `https://${hostname("app")}`,
    workspaceDomain: hostname("ws"),
    signInDomain: hostname("auth"),
  };
};

export const CONNECT_PROFILES: Readonly<Record<ConnectStage, ConnectProfile>> = {
  preview: {
    ...stageHostnames(".preview"),
    oauthClientId: "afar-connect-preview",
    developmentWebOrigin: CONNECT_WEB_DEVELOPMENT_ORIGIN,
  },
  production: {
    ...stageHostnames(""),
    oauthClientId: "afar-connect-production",
  },
};

/** Where the web client's sign-in returns, on the page's own origin. */
export const CONNECT_WEB_CALLBACK_PATH = "/connect-callback.html";

/** The loopback listener the Desktop Client's sign-in returns to. */
export const CONNECT_DESKTOP_CALLBACK_HOST = "127.0.0.1";
export const CONNECT_DESKTOP_CALLBACK_PORT = 43127;
export const CONNECT_DESKTOP_CALLBACK_PATH = "/connect/callback";
export const CONNECT_DESKTOP_CALLBACK_URL = `http://${CONNECT_DESKTOP_CALLBACK_HOST}:${CONNECT_DESKTOP_CALLBACK_PORT}${CONNECT_DESKTOP_CALLBACK_PATH}`;

/** Every callback a Connect deployment must accept for its own clients. */
export const connectProfileRedirects = (profile: ConnectProfile): readonly string[] => [
  `${profile.webOrigin}${CONNECT_WEB_CALLBACK_PATH}`,
  ...(profile.developmentWebOrigin
    ? [`${profile.developmentWebOrigin}${CONNECT_WEB_CALLBACK_PATH}`]
    : []),
  CONNECT_DESKTOP_CALLBACK_URL,
];

/**
 * The build-time environment a client bundle reads its Connect settings from. There is no separate
 * issuer setting: clients sign in to Connect, which signs people in with Google behind it, so its
 * origin is the issuer.
 */
export const connectProfileClientEnvironment = (
  profile: ConnectProfile,
): Readonly<Record<string, string>> => ({
  AFAR_CONNECT_ORIGIN: profile.origin,
  AFAR_CONNECT_OAUTH_CLIENT_ID: profile.oauthClientId,
  AFAR_CONNECT_BROWSER_ORIGIN: profile.webOrigin,
});
